import {
  Agent,
  AllDocumentationRequired,
  BorrowerDocumentationRequired,
  BorrowerFileType,
  BorrowerOnboardingDetails,
  CheckoutSession,
  DocumentationRequiredType,
  DocumentStatus,
  EditableField,
  getAllDocumentationRequiredByLoanId,
  getKycIntegrationMethod,
  getLoanById,
  getPaymentMethodModificationCapability,
  IntegrationType,
  KycIntegrationMethodStatus,
  KycIntegrationMethodType,
  LenderApprovalStatus,
  LenderName,
  Loan,
  OnboardingDetailsRole,
  OnboardingFulfilledBy,
  patchCreateApplicationsByPaymentMethodType,
  patchSetAreDocumentsConfirmed,
  patchSetOnboardingDetailsAndCreateApplications,
  patchUpdateBorrowerDetails,
  PaymentIntent,
  PaymentMethodModificationCapability,
  PaymentMethodType,
  postSyncLoanWithLender,
  RequiredFile,
} from '@nemuru-eng/js-api-client';
import { v4 as uuidv4 } from 'uuid';

import { CartSummaryMap } from '@/components/modules/_shared/CartSummary/maps/CartSummary.map';
import { AbstractLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractLoanPaymentMethod';
import { SetupOptions } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractPaymentMethod';
import { PaymentMethodFactory } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/PaymentMethodFactory';
import {
  AutoLoanPaymentMethod,
  AutoPaymentMethod,
} from '@/components/modules/Application/components/PaymentMethod/AutoLoan/entities/PaymentMethod/AutoLoanPaymentMethod';
import { Action } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/types/PendingAction';
import { AbstractLoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/entities/PaymentMethodDetail/AbstractLoanPaymentMethodDetailEntity';
import { WaitForDocumentationRequiredTimeoutException } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/exceptions/WaitForDocumentationRequiredTimeoutException';
import {
  CustomRequiredFile,
  KycIntegrationMethodOwner,
  OwnedKycIntegrationMethod,
  StoredBorrowerOnboardingDetails,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/types';
import { patchNieDataIfRequired } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/utils/patchNieDataIfRequired';
import { ActionsMap } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/NextActions/maps/actions.map';
import {
  doesEditableFieldExistInArray,
  hasEditableFields,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/maps/editableFieldToFormField.map';
import { LoanStatusExternalMap } from '@/maps/LoanStatusExternal.map';

const POLLING_INTERVAL_MS = 3000; // 3 seconds
const LENDERS_ALLOW_ADD_COBORROWER = [LenderName.CAIXABANK, LenderName.BBVA];

export class LoanPaymentMethodDetailEntity extends AbstractLoanPaymentMethodDetailEntity {
  static allowed = [
    PaymentMethodType.AUTO_LOAN,
    PaymentMethodType.B2B_LOAN,
    PaymentMethodType.CONSUMER_LOAN_INSTALMENTS,
    PaymentMethodType.CONSUMER_LOAN_SPLIT_PAY,
  ];

  private _allDocumentationRequired: AllDocumentationRequired | undefined;
  private _kycIntegrationMethods: OwnedKycIntegrationMethod[] | undefined;
  private _modificationCapability: PaymentMethodModificationCapability | undefined;

  constructor(
    paymentMethodType: PaymentMethodType,
    checkoutSession: CheckoutSession,
    agent: Agent,
    paymentIntent?: PaymentIntent,
  ) {
    super(paymentMethodType, checkoutSession, agent, paymentIntent);
  }

  public static isAllowed(type: PaymentMethodType): boolean {
    return this.allowed.includes(type);
  }

  public reset(): this {
    this._allDocumentationRequired = undefined;
    this._modificationCapability = undefined;
    return super.reset();
  }

  public get modificationCapability(): PaymentMethodModificationCapability | undefined {
    return this._modificationCapability;
  }

  private shouldFetchModificationCapability(): boolean {
    if (!this.isAuto() || !this.paymentMethodId) {
      return false;
    }

    const cartSummaryData = CartSummaryMap.fromCheckoutSessionToCartSummaryData(this.checkoutSession);

    return CartSummaryMap.isSingleAutomobileCart(cartSummaryData);
  }

  public async refreshModificationCapability(): Promise<this> {
    if (!this.shouldFetchModificationCapability() || !this.paymentMethodId) {
      this._modificationCapability = undefined;
      return this;
    }

    try {
      this._modificationCapability = await getPaymentMethodModificationCapability({
        checkoutSessionId: this.checkoutSession.id,
        paymentMethodId: this.paymentMethodId,
      });
    } catch (error) {
      console.error('[LoanPaymentMethodDetailEntity.refreshModificationCapability]: ', error);
      this._modificationCapability = undefined;
    }

    return this;
  }

  public isVehicleDataEditable(): boolean {
    if (!this.isAuto()) {
      return false;
    }

    if (!this.modificationCapability) {
      return false;
    }

    return (
      hasEditableFields(this.modificationCapability.editableFields) &&
      this.modificationCapability.modifiable &&
      this.modificationCapability.lenderAllowsModification
    );
  }

  private getSingleAutomobileCartSummaryLine() {
    if (!this.isAuto()) {
      return undefined;
    }

    const cartSummaryData = CartSummaryMap.fromCheckoutSessionToCartSummaryData(this.checkoutSession);

    if (!CartSummaryMap.isSingleAutomobileCart(cartSummaryData)) {
      return undefined;
    }

    return cartSummaryData.cartSummaryLines[0];
  }

  public isMissingVehicleRegistrationNumber(): boolean {
    const autoLine = this.getSingleAutomobileCartSummaryLine();

    if (!autoLine) {
      return false;
    }

    return !autoLine.identificationNumber;
  }

  public isMissingVehicleLicensePlate(): boolean {
    const autoLine = this.getSingleAutomobileCartSummaryLine();

    if (!autoLine) {
      return false;
    }

    return !autoLine.isNewVehicle && !autoLine.plateNumber;
  }

  public isMissingVehicleRegistrationNumberOrLicensePlate(): boolean {
    return this.isMissingVehicleRegistrationNumber() || this.isMissingVehicleLicensePlate();
  }

  public isVehicleAmountEditable(): boolean {
    if (!this.isVehicleDataEditable() || !this.modificationCapability) {
      return false;
    }

    return doesEditableFieldExistInArray(this.modificationCapability.editableFields, EditableField.VEHICLE_AMOUNT);
  }

  public async createOrUpdateOnboardingDetails(onboardingDetails: StoredBorrowerOnboardingDetails): Promise<this> {
    if (!this.paymentIntentId) {
      throw new Error('Payment intent id is required to create or update onboarding details');
    }

    if (!this._loan) {
      await this.refreshLoan();
    }

    const loan = this._loan;

    if (!loan) {
      throw new Error('Loan is required to create or update onboarding details');
    }

    const paymentIntentOnboardingDetails = loan.onboardingDetails.find((od) => od.role === onboardingDetails.role);

    const onboardingDetailsId = paymentIntentOnboardingDetails?.id ?? onboardingDetails.id;

    await patchUpdateBorrowerDetails({
      ...onboardingDetails,
      paymentIntentId: this.paymentIntentId,
      onboardingDetailsId: onboardingDetailsId,
      id: this.checkoutSession.id,
      role: onboardingDetails.role,
    });

    const refreshedOnboardingDetailsId = this.onboardingDetails(onboardingDetails.role)?.id ?? onboardingDetailsId;

    await patchNieDataIfRequired({
      onboardingDetails,
      onboardingDetailsId: refreshedOnboardingDetailsId,
      paymentMethodDetailEntity: this,
    });

    await this.refreshLoan();

    return this;
  }

  public async createApplications(
    clientSecret: string,
    onboardingDetails: StoredBorrowerOnboardingDetails[],
  ): Promise<this> {
    let paymentMethodId = this.paymentMethodId;

    const borrower = onboardingDetails.find((borrower) => borrower.role === OnboardingDetailsRole.BORROWER)!;
    const coBorrower = onboardingDetails.find((borrower) => borrower.role === OnboardingDetailsRole.CO_BORROWER);
    const guarantor = onboardingDetails.find((borrower) => borrower.role === OnboardingDetailsRole.GUARANTOR);

    if (!paymentMethodId) {
      paymentMethodId = uuidv4();
      await patchCreateApplicationsByPaymentMethodType({
        clientSecret,
        id: this.checkoutSession.id,
        paymentMethod: {
          type: this.type,
          id: paymentMethodId,
        },
        borrower,
        ...(coBorrower && { coBorrower }),
        ...(guarantor && { guarantor }),
      });
    } else {
      await patchSetOnboardingDetailsAndCreateApplications({
        clientSecret,
        id: this.checkoutSession.id,
        paymentMethodType: this.type,
        borrower,
        ...(coBorrower && { coBorrower }),
        ...(guarantor && { guarantor }),
      });
    }

    this.paymentMethodId = paymentMethodId;

    return this;
  }

  public async syncWithLender(): Promise<void> {
    if (!this.paymentMethodId) {
      return;
    }

    if (!this._loan || !this._loan.loanApplicationSelected) {
      return;
    }

    try {
      await postSyncLoanWithLender({ id: this.paymentMethodId });
    } catch {}
  }

  public async refresh(): Promise<this> {
    if (!this.paymentMethodId) {
      if (!this.paymentMethod) {
        try {
          this.setPaymentMethodFromCalculator();
        } catch (error) {
          this.setError(error as Error);
        }
      }
      this._modificationCapability = undefined;
      this._isRefreshed = true;
      return this;
    }

    const capabilityPromise = this.refreshModificationCapability();

    try {
      const loan = await getLoanById({ loanId: this.paymentMethodId });
      await this.refreshDataFromLoan(loan);
    } catch (error) {
      this.setError(error as Error);
    }

    await capabilityPromise;
    this._isRefreshed = true;
    return this;
  }

  public async refreshLoan(): Promise<this> {
    if (!this.paymentMethodId) {
      return this;
    }
    const loan = await getLoanById({ loanId: this.paymentMethodId });
    this._loan = loan;
    return this;
  }

  public async refreshDataFromLoan(loan: Loan): Promise<this> {
    await this.setPaymentMethodFromLoan(loan);
    this._loan = loan;
    this._allDocumentationRequired = await this.getDocumentationRequired();
    await this.refreshKycIntegrationMethods();
    this.hasProviderError = this.getHasProviderError();
    this.actions = (await this.getPendingActions()) ?? undefined;
    this._progress = await this.getProgress();
    return this;
  }

  protected async setPaymentMethodFromLoan(loan: Loan): Promise<void> {
    const paymentMethodEntity = PaymentMethodFactory.byType(this.type) as AbstractLoanPaymentMethod;

    if (!(paymentMethodEntity instanceof AbstractLoanPaymentMethod)) {
      throw new Error('Payment method entity is not a loan payment method');
    }

    let lenderNames: LenderName[] | undefined;

    if (loan.loanApplicationSelected) {
      lenderNames = [loan.loanApplicationSelected.lenderName];
    }

    // Costly setup is only performed when needed
    if (!loan.loanApplicationSelected) {
      const totalToFinance = this.totalToFinance();

      const term = loan.loanConditions.loanCalculator.term.period;

      let options: SetupOptions | undefined;
      if (term) {
        options = { terms: [term] };
      }

      paymentMethodEntity.setup(totalToFinance.value, totalToFinance.currency, options);

      if (this.isAuto() && this.paymentMethodOptions && paymentMethodEntity instanceof AutoLoanPaymentMethod) {
        paymentMethodEntity.setConditionsFromPaymentMethodOptions(
          this.paymentMethodOptions as AutoPaymentMethod,
          this.loan,
        );
      }

      const candidateLenderNames = paymentMethodEntity.getAllProviderNames();
      lenderNames = candidateLenderNames;

      const processingStrategyLenderNames = this.getLenderNamesFromProcessingStrategy();

      if (processingStrategyLenderNames.length > 0) {
        lenderNames = candidateLenderNames.filter((lenderName) => processingStrategyLenderNames.includes(lenderName));
      }
    }

    paymentMethodEntity.setConditionFromLoanConditions(loan, lenderNames);

    this.paymentMethod = paymentMethodEntity;
  }

  public setPaymentMethodFromCalculator(considerAllTerms: boolean = false): void {
    const paymentMethodEntity = PaymentMethodFactory.byType(this.type);

    if (!(paymentMethodEntity instanceof AbstractLoanPaymentMethod)) {
      throw new Error('Payment method entity is not a loan payment method');
    }

    const totalToFinance = this.totalToFinance();

    if (considerAllTerms) {
      paymentMethodEntity.setup(totalToFinance.value, totalToFinance.currency);
    } else {
      let terms: number[] | undefined;
      const paymentMethod = this.checkoutSession.paymentMethods.find((pm) => pm.type === this.type);
      if (
        paymentMethod &&
        'options' in paymentMethod &&
        paymentMethod.options &&
        'period' in paymentMethod.options &&
        paymentMethod.options.period
      ) {
        terms = [paymentMethod.options.period];
      }

      const options: SetupOptions = { terms };

      paymentMethodEntity.setup(totalToFinance.value, totalToFinance.currency, options);
    }

    if (this.isAuto() && this.paymentMethodOptions && paymentMethodEntity instanceof AutoLoanPaymentMethod) {
      paymentMethodEntity.setConditionsFromPaymentMethodOptions(
        this.paymentMethodOptions as AutoPaymentMethod,
        this.loan,
      );
      this.paymentMethod = paymentMethodEntity;
      return;
    }

    this.updateTerm(paymentMethodEntity);

    this.paymentMethod = paymentMethodEntity;
  }

  public isSignatureEmbeddable(integrationType: IntegrationType): boolean {
    const loan = this._loan;

    if (integrationType === IntegrationType.EMBEDDED_SAML) {
      return true;
    }

    if (!loan || !loan.loanApplicationSelected) {
      return false;
    }

    // TODO: Discarded unless Santander supports auto-login
    //if (
    //  integrationType === IntegrationType.REDIRECT &&
    //  loan.loanApplicationSelected.lenderName === LenderName.SANTANDER_CONSUMER
    //) {
    //  return true;
    //}

    return false;
  }

  public async waitUntilDocumentationRequiredFilesExist(): Promise<this> {
    const documentationRequired = await this.getDocumentationRequiredIfRequiredFilesExist();
    this._allDocumentationRequired = documentationRequired;
    return this;
  }

  public async waitUntilSigned(): Promise<this> {
    const loan = await this.getLoanIfLoanApplicationIsSigned();
    const loanStatusExternal = LoanStatusExternalMap.fromLoanStatus(loan.status);
    this.status = loanStatusExternal;
    return await this.refreshDataFromLoan(loan);
  }

  private async getLoanIfLoanApplicationIsSigned(): Promise<Loan> {
    const paymentId = this.paymentMethodId;
    if (!paymentId) {
      throw new Error('Payment method id is missing');
    }

    let attempts = 0;
    return new Promise((resolve, reject) => {
      (async function waitForSignedApplication() {
        attempts += 1;
        if (attempts > 30) {
          return reject();
        }

        try {
          await postSyncLoanWithLender({ id: paymentId });
        } catch {}
        const loan = await getLoanById({ loanId: paymentId });

        if (loan.loanApplicationSelected?.loanLenderBorrowerContract.isSigned) {
          return resolve(loan);
        }

        setTimeout(waitForSignedApplication, POLLING_INTERVAL_MS);
      })();
    });
  }

  public async waitUntilRequiredFileUploaded(requiredFile: CustomRequiredFile): Promise<this> {
    await patchSetAreDocumentsConfirmed({
      id: requiredFile.documentationRequiredId,
      documentationRequiredType: requiredFile.type,
      areDocumentsConfirmed: true,
    });
    const allDocumentationRequired = await this.getDocumentationRequiredIfUploaded(requiredFile);
    this._allDocumentationRequired = this.getFilteredDocumentationRequired(allDocumentationRequired);
    return this;
  }

  private async getDocumentationRequired(): Promise<AllDocumentationRequired | undefined> {
    if (!this.paymentMethodId) {
      return;
    }

    if (!this._loan || !this._loan.loanApplicationSelected) {
      return;
    }

    const allDocumentationRequired = await getAllDocumentationRequiredByLoanId({ loanId: this.paymentMethodId });
    return this.getFilteredDocumentationRequired(allDocumentationRequired);
  }

  private getFilteredDocumentationRequired(
    allDocumentationRequired: AllDocumentationRequired,
  ): AllDocumentationRequired {
    return {
      ...allDocumentationRequired,
      loan: {
        ...allDocumentationRequired.loan,
        ...(allDocumentationRequired.loan?.requiredFiles && {
          requiredFiles: this.filterExcludedRequiredFiles(allDocumentationRequired.loan.requiredFiles),
        }),
      },
      borrowers: allDocumentationRequired.borrowers.map((borrower) => ({
        ...borrower,
        ...(borrower.requiredFiles && {
          requiredFiles: this.filterExcludedRequiredFiles(borrower.requiredFiles),
        }),
      })),
    } as AllDocumentationRequired;
  }

  private filterExcludedRequiredFiles(requiredFiles: RequiredFile[]): RequiredFile[] {
    return requiredFiles.filter(
      (file) =>
        file.name !== BorrowerFileType.BIOMETRICS_DOCUMENT &&
        file.name !== BorrowerFileType.KYC_VIDEO &&
        file.name !== BorrowerFileType.BANK_READER_INCOME &&
        file.name !== BorrowerFileType.BORROWER_BANK_READER,
    );
  }

  private async getDocumentationRequiredIfUploaded(
    requiredFile: CustomRequiredFile,
  ): Promise<AllDocumentationRequired> {
    const paymentId = this.paymentMethodId;

    if (!paymentId) {
      throw new Error('Payment method id is missing');
    }

    let attempts = 0;

    return new Promise((resolve, reject) => {
      (async function waitForUploadedDocumentation() {
        attempts += 1;
        if (attempts > 6) {
          return reject();
        }

        const documentation = await getAllDocumentationRequiredByLoanId({ loanId: paymentId });

        const documentationRequiredType = requiredFile.type;

        if (documentationRequiredType === DocumentationRequiredType.LOAN && documentation.loan) {
          const found = documentation.loan.requiredFiles?.some(
            (file) => file.name === requiredFile.name && file.status === DocumentStatus.UPLOADED,
          );
          if (found) {
            return resolve(documentation);
          }
        }

        if (documentationRequiredType === DocumentationRequiredType.BORROWER && documentation.borrowers) {
          for (const borrower of documentation.borrowers) {
            const found = borrower.requiredFiles?.some(
              (file) => file.name === requiredFile.name && file.status === DocumentStatus.UPLOADED,
            );
            if (found) {
              return resolve(documentation);
            }
          }
        }

        setTimeout(waitForUploadedDocumentation, POLLING_INTERVAL_MS);
      })();
    });
  }

  private async getDocumentationRequiredIfRequiredFilesExist(): Promise<AllDocumentationRequired> {
    const paymentId = this.paymentMethodId;

    if (!paymentId) {
      throw new Error('Payment method id is missing');
    }

    let attempts = 0;

    return new Promise((resolve, reject) => {
      (async function waitForUploadedDocumentation() {
        attempts += 1;
        if (attempts > 10) {
          return reject(new WaitForDocumentationRequiredTimeoutException());
        }

        const documentation = await getAllDocumentationRequiredByLoanId({ loanId: paymentId });

        if (documentation.borrowers.some((borrower) => borrower.requiredFiles && borrower.requiredFiles.length > 0)) {
          return resolve(documentation);
        }

        setTimeout(waitForUploadedDocumentation, 2000);
      })();
    });
  }

  public get allRequiredFiles(): CustomRequiredFile[] | undefined {
    const allDocumentationRequired = this._allDocumentationRequired;

    if (!allDocumentationRequired) {
      return;
    }

    const loanRequiredFiles = allDocumentationRequired.loan?.requiredFiles?.map((file) => ({
      ...file,
      type: DocumentationRequiredType.LOAN,
      documentationRequiredId: allDocumentationRequired!.loan!.id,
    })) as CustomRequiredFile[];

    const borrowerRequiredFiles = allDocumentationRequired.borrowers.flatMap((borrower) =>
      borrower.requiredFiles?.map((file) => ({
        ...file,
        type: DocumentationRequiredType.BORROWER,
        documentationRequiredId: borrower.id,
        borrowerId: borrower.borrowerId,
      })),
    ) as CustomRequiredFile[];

    return [...(loanRequiredFiles ?? []), ...(borrowerRequiredFiles ?? [])];
  }

  public get borrowerDocumentationRequired(): BorrowerDocumentationRequired | undefined {
    const allDocumentationRequired = this._allDocumentationRequired;
    const loan = this._loan;

    if (!allDocumentationRequired || !loan) {
      return undefined;
    }

    return allDocumentationRequired.borrowers.find((data) => data.borrowerId === loan.borrowerId);
  }

  public async isBorrowerDocumentationUploaded(): Promise<boolean> {
    if (!this._allDocumentationRequired) {
      this._allDocumentationRequired = await this.getDocumentationRequired();
    }

    if (!this._loan) return false;
    if (!this.borrowerDocumentationRequired) return false;

    const requiredFiles = this.getAllRequiredFiles();

    const someFilePending = requiredFiles.some(
      (file) => file.status === DocumentStatus.PENDING || file.status === DocumentStatus.INCIDENCE,
    );

    if (someFilePending) return false;

    return true;
  }

  public get kycIntegrationMethods(): OwnedKycIntegrationMethod[] | undefined {
    return this._kycIntegrationMethods;
  }

  public async refreshKycIntegrationMethods(): Promise<void> {
    try {
      const [borrowerKycIntegrationMethod, coBorrowerKycIntegrationMethod] = await Promise.all([
        this.getKycIntegrationMethod(KycIntegrationMethodOwner.BORROWER),
        this.getKycIntegrationMethod(KycIntegrationMethodOwner.COWORROWER),
      ]);

      if (borrowerKycIntegrationMethod) {
        this._kycIntegrationMethods = [borrowerKycIntegrationMethod];

        if (coBorrowerKycIntegrationMethod) {
          this._kycIntegrationMethods.push(coBorrowerKycIntegrationMethod);
        }
      }
    } catch (error) {
      console.error('[LoanPaymentMethodDetailEntity.refreshKycIntegrationMethods]: ', error);
      // TODO: error tracking with posthog
    }
  }

  public async getKycIntegrationMethod(
    owner: KycIntegrationMethodOwner,
  ): Promise<OwnedKycIntegrationMethod | undefined> {
    const loan = this.loan;
    const onboardingDetails =
      owner === KycIntegrationMethodOwner.BORROWER
        ? this.borrowerOnboardingDetails()
        : this.coBorrowerOnboardingDetails();

    if (!loan || !onboardingDetails) {
      return undefined;
    }

    const kycIntegrationMethod = await getKycIntegrationMethod({
      loanId: loan.id,
      onboardingDetailsId: onboardingDetails.id,
    });

    return {
      ...kycIntegrationMethod,
      owner,
    };
  }

  public hasAllKycBeenCompleted(): boolean {
    if (!this._kycIntegrationMethods?.length) {
      return false;
    }

    return this._kycIntegrationMethods.every(
      (kyc) =>
        kyc.status === KycIntegrationMethodStatus.COMPLETED || kyc.status === KycIntegrationMethodStatus.ACCEPTED,
    );
  }

  public hasSomeKycFailed(): boolean {
    if (!this._kycIntegrationMethods?.length) {
      return false;
    }

    return this._kycIntegrationMethods.some((kyc) => kyc.status === KycIntegrationMethodStatus.FAILED);
  }

  public doesRequireKyc(): boolean {
    if (!this._kycIntegrationMethods?.length) {
      return false;
    }

    return this._kycIntegrationMethods.every((kyc) => [KycIntegrationMethodType.EMAIL].includes(kyc.integrationType));
  }

  private getAllRequiredFiles(): RequiredFile[] {
    if (!this._allDocumentationRequired || !this.loan) {
      return [];
    }
    const loanRequiredFiles = this._allDocumentationRequired.loan?.requiredFiles ?? [];
    const borrowerRequiredFiles = this.borrowerDocumentationRequired!.requiredFiles ?? [];
    return [...loanRequiredFiles, ...borrowerRequiredFiles];
  }

  private isLoanApplicationSelected(): boolean {
    return (
      !!this._loan?.loanApplicationSelected?.lenderFields.lenderRequestId &&
      (this._loan?.loanApplicationSelected.lenderFields.loanApplicationStatus.lenderApprovalStatus ===
        LenderApprovalStatus.YES ||
        this._loan?.loanApplicationSelected.lenderFields.loanApplicationStatus.lenderApprovalStatus ===
          LenderApprovalStatus.MAYBE)
    );
  }

  public async isBorrowerDocumentationSentToLender(): Promise<boolean> {
    if (!this._loan || !this._loan.loanApplicationSelected) return false;
    if (!this.borrowerDocumentationRequired) return false;
    if (this._loan.loanApplicationSelected.isDocumentationUploadedToLender) return true;
    return false;
  }

  public get borrowerDocumentationHasIncidence(): boolean {
    if (this.hasProviderError) return false;
    return !!(this._loan?.loanApplicationSelected && this._loan.loanApplicationSelected.isDocumentationWithIncidence);
  }

  public hasCoborrower(): boolean {
    if (!this.loan) return false;
    if (this.loan?.onboardingDetails.length === 0) return false;

    return this.loan.onboardingDetails.some((b) => b.role === OnboardingDetailsRole.CO_BORROWER);
  }

  public allowAddCoborrower(): boolean {
    if (this.isFinalStatus()) return false;
    if (!this._loan || !this._loan.loanApplicationSelected) return false;
    if (!this._loan.loanApplicationSelected.lenderFields.lenderRequestId) return true;

    if (LENDERS_ALLOW_ADD_COBORROWER.includes(this._loan.loanApplicationSelected.lenderName)) {
      return this.isFinalStatus() === false;
    }

    return false;
  }

  public allowEditBorrower(): boolean {
    if (this.isFinalStatus()) return false;
    if (!this.loan) return false;
    if (!this.loan.loanApplicationIds) return false;
    if (this.loan.onboardingDetails.length === 0) return false;

    const borrowerOnboardingDetails = this.loan.onboardingDetails.find(
      (b) => b.role === OnboardingDetailsRole.BORROWER,
    );

    if (!borrowerOnboardingDetails) {
      return false;
    }

    return borrowerOnboardingDetails.isCompleted === true;
  }

  public allowEditCoBorrower(): boolean {
    if (this.isFinalStatus()) return false;
    if (!this.loan) return false;
    if (!this.loan.loanApplicationIds) return false;
    if (!this.coBorrowerOnboardingDetails()) return false;
    if (this.isLoanApplicationSelected()) return false;

    return true;
  }

  public isMerchantPos(): boolean {
    return this.agent.allowedOnboardingFulfilledByTypes.includes(OnboardingFulfilledBy.MERCHANT_POS);
  }

  public hasOnboardingDetails(role?: OnboardingDetailsRole): boolean {
    if (!this.loan) {
      return false;
    }

    if (role) {
      return this.loan.onboardingDetails.some((b) => b.role === role);
    }

    return this.loan.onboardingDetails.length > 0;
  }

  public onboardingDetails(role: OnboardingDetailsRole): BorrowerOnboardingDetails | null {
    if (this.hasOnboardingDetails(role) === false) {
      return null;
    }

    return this.loan!.onboardingDetails.find((b) => b.role === role) as BorrowerOnboardingDetails;
  }

  public borrowerOnboardingDetails(): BorrowerOnboardingDetails | null {
    if (this.hasOnboardingDetails(OnboardingDetailsRole.BORROWER) === false) {
      return null;
    }

    return this.loan!.onboardingDetails.find(
      (b) => b.role === OnboardingDetailsRole.BORROWER,
    ) as BorrowerOnboardingDetails;
  }

  public coBorrowerOnboardingDetails(): BorrowerOnboardingDetails | null {
    if (this.hasOnboardingDetails(OnboardingDetailsRole.CO_BORROWER) === false) {
      return null;
    }

    return this.loan!.onboardingDetails.find(
      (b) => b.role === OnboardingDetailsRole.CO_BORROWER,
    ) as BorrowerOnboardingDetails;
  }

  public guarantorOnboardingDetails(): BorrowerOnboardingDetails | null {
    if (this.hasOnboardingDetails(OnboardingDetailsRole.GUARANTOR) === false) {
      return null;
    }

    return this.loan!.onboardingDetails.find(
      (b) => b.role === OnboardingDetailsRole.GUARANTOR,
    ) as BorrowerOnboardingDetails;
  }

  public async isActionCompleted(action: Action): Promise<boolean> {
    if (!this._loan) {
      return false;
    }
    if (action === Action.COMPLETE_ONBOARDING) {
      return this.isRequested() || this.isConfirmed();
    }
    if (action === Action.UPLOAD_DOCUMENTS) {
      return await this.isBorrowerDocumentationUploaded();
    }
    if (action === Action.SIGN_CONTRACT || action === Action.COMPLETE_PROCESS_SANTANDER) {
      return this.isSigned();
    }
    if (action === Action.NO_LENDER_REQUEST_ID) {
      return false;
    }

    if (action === Action.COMPLETE_KYC) {
      return this.hasAllKycBeenCompleted();
    }

    throw new Error(`Action ${action} is not supported`);
  }

  private async getPendingActions(): Promise<Action[] | null> {
    return await ActionsMap.fromPaymentMethodDetail(this);
  }

  // React context triggers re-renders only when the value passed to the context provider changes (i.e., when the reference changes).
  // However, there are some caveats:
  // Mutating a class instance (e.g., calling methods that change its internal state) does not change the reference.
  // If you do something like entity.refresh() and then call setContextValue(entity), React sees the same object reference and does not re-render consumers.
  // Class instances are "weird" for React because React can't track their internal state changes. React only tracks reference changes for context and props.
  // When you want to update the context with a new state, create a new instance of the class (or a new object) and set that in the context. For example:
  public clone(): this {
    const clone = new LoanPaymentMethodDetailEntity(this.type, this.checkoutSession, this.agent);

    // Copy all properties from this to clone
    Object.assign(clone, this);

    return clone as this;
  }
}
